import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ContentCopy from "@mui/icons-material/ContentCopy";
import {
  useCreateInboxAddress,
  useInboxAddress,
  useRotateInboxAddress,
  useUpdateInboxAllowedFromEmails,
} from "@seabrookstudios/pitch2table";
import { enqueueSnackbar } from "notistack";
import { useCopyToClipboard } from "usehooks-ts";
import { AlwaysOnAppLink } from "../styleguide/AppLink";
import { Path } from "../Path";
import { IsMember, LockedBanner } from "../core/HasEntitlements";
import { useHasSlug } from "../sessions/model";
import { EditableTagsListItem } from "../games/EditableTagsListItem";

const CreateInboxAddress = () => {
  const hasUserSlug = useHasSlug();
  const createAddress = useCreateInboxAddress(
    () => enqueueSnackbar("Inbox address created", { variant: "success" }),
    () => enqueueSnackbar("Could not create inbox address", { variant: "error" })
  );

  if (!hasUserSlug) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        We need a username to create an inbox address. Please set your username in{" "}
        <AlwaysOnAppLink to={Path.settings().category("about-you")}>settings</AlwaysOnAppLink> before creating an inbox
        address.
      </Alert>
    );
  }

  return (
    <ListItem>
      <ListItemText primary="Pitch2Table Inbox Email" secondary="Not created" />
      <ListItemIcon>
        <Box justifyContent="center" alignItems="center" display="flex" flex={1}>
          <Button variant="contained" onClick={() => createAddress.mutate()} disabled={createAddress.isPending}>
            Create
          </Button>
        </Box>
      </ListItemIcon>
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {string} props.email
 * @param {string} [props.allowedFromEmails]
 */
const InboxAddress = ({ email, allowedFromEmails = "" }) => {
  const rotateAddress = useRotateInboxAddress(
    () => enqueueSnackbar("Inbox address rotated", { variant: "success" }),
    () => enqueueSnackbar("Could not rotate inbox address", { variant: "error" })
  );
  const updateAllowedFrom = useUpdateInboxAllowedFromEmails(
    () => enqueueSnackbar("Allowed from addresses updated", { variant: "success" }),
    () => enqueueSnackbar("Could not update allowed from addresses", { variant: "error" })
  );
  const [, copy] = useCopyToClipboard();

  const onCopy = () => {
    copy(email);
    enqueueSnackbar("Address copied to clipboard", { variant: "info" });
  };

  const onRotate = () => {
    if (!window.confirm("Rotate your inbox address? The old email address will no longer receive responses.")) {
      return;
    }
    rotateAddress.mutate();
  };

  /**
   * @param {string} text
   */
  const onSaveAllowedFrom = async (text) => {
    await updateAllowedFrom.mutateAsync({ allowedFromEmails: text });
  };

  return (
    <>
      <ListItem
        secondaryAction={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton edge="end" onClick={onCopy} aria-label="copy inbox address">
              <ContentCopy />
            </IconButton>
            <Button size="small" onClick={onRotate} disabled={rotateAddress.isPending}>
              Rotate
            </Button>
          </Stack>
        }
      >
        <ListItemText
          primary="Your inbox address"
          secondary={
            <Typography variant="body2" sx={{ wordBreak: "break-all" }} component="span">
              {email}
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <EditableTagsListItem
          title="Additional allowed from addresses"
          text={allowedFromEmails}
          onSave={onSaveAllowedFrom}
          helperText="Comma-separated extra addresses that are allowed to send to your inbox address. Your account email is always allowed."
        />
      </ListItem>
    </>
  );
};

const EmailInboxMembers = () => {
  const { data: address, isLoading } = useInboxAddress();

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={1} square={false}>
      <CardContent>
        <Alert severity="info">
          Use the Pitch2Table inbox email address to capture publisher responses and link them to your publisher
          submissions. Unless linked, all captured emails are deleted after 30 days. New threads must be started from
          your account email or an allowed from address below.
        </Alert>
        <List>
          {address?.email ? (
            <InboxAddress email={address.email} allowedFromEmails={address.allowedFromEmails} />
          ) : (
            <CreateInboxAddress />
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export const EmailInbox = () => (
  <IsMember
    LockedView={<LockedBanner title="Email Inbox is a Premium feature. Upgrade to set up your inbox address." />}
  >
    <EmailInboxMembers />
  </IsMember>
);
